import { useState } from 'react';
import { App, Form, Input, Modal, Select, Skeleton, Switch } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listAdmins,
  createAdmin,
  patchAdmin,
  enableAdmin,
  disableAdmin,
  resetAdminPassword,
  type AdminRow,
} from '../api/platform-admin';
import { Topbar } from '../layouts/Topbar';
import { formatDateTime } from '../utils/datetime';

const ROLE_LABEL: Record<AdminRow['role'], string> = {
  admin: '管理员',
  operator: '运营',
};

export function AdminsPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminRow | null>(null);
  const [resetTarget, setResetTarget] = useState<AdminRow | null>(null);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['admins'],
    queryFn: () => listAdmins({ limit: 100, offset: 0 }),
  });

  const rows = data?.rows ?? [];

  const showError = (e: unknown) => {
    const msg = (e as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
      ?? (e as Error).message
      ?? '操作失败';
    message.error(msg);
  };
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admins'] });

  const toggleEnabled = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      enabled ? enableAdmin(id) : disableAdmin(id),
    onSuccess: () => { invalidate(); message.success('已更新'); },
    onError: showError,
  });

  return (
    <>
      <Topbar crumbs={[{ label: '系统' }, { label: '用户管理' }]} />
      <div className="page">
        <div className="page-header">
          <div className="page-header__title">
            <h1>用户管理</h1>
            <div className="page-header__sub">管理 Console 后台账号、角色与启用状态。</div>
          </div>
          <div className="page-header__actions">
            <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
              <PlusOutlined /> <span>新建用户</span>
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card__body" style={{ paddingBottom: 0 }}>
            <div className="toolbar">
              <div className="toolbar__right">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => refetch()} disabled={isFetching}>
                  <ReloadOutlined spin={isFetching} /> <span>刷新</span>
                </button>
              </div>
            </div>
          </div>

          {isLoading ? <div style={{ padding: 24 }}><Skeleton active /></div> : (
            <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
              <table className="table table--fixed">
                <colgroup>
                  <col style={{ width: '24%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: 120 }} />
                  <col style={{ width: 120 }} />
                  <col style={{ width: 190 }} />
                  <col style={{ width: 180 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>用户名</th>
                    <th>显示名</th>
                    <th>角色</th>
                    <th>启用</th>
                    <th>创建时间</th>
                    <th className="actions">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <div className="text-strong">{row.username}</div>
                      </td>
                      <td>{row.displayName || <span className="text-muted">—</span>}</td>
                      <td><span className={`tag tag--role-${row.role}`}>{ROLE_LABEL[row.role]}</span></td>
                      <td>
                        <Switch
                          checked={row.enabled}
                          loading={toggleEnabled.isPending}
                          onChange={(checked) => toggleEnabled.mutate({ id: row.id, enabled: checked })}
                        />
                      </td>
                      <td className="text-mono num text-xs" title={row.createdAt}>{formatDateTime(row.createdAt)}</td>
                      <td className="actions">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditTarget(row)}>编辑</button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setResetTarget(row)}>重置密码</button>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', color: 'var(--c-fg-3)', padding: 24 }}>暂无数据</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="pagination">
            <span className="text-muted">共 <strong>{data?.total ?? 0}</strong> 条</span>
          </div>
        </div>
      </div>
      <CreateModal open={createOpen} onClose={() => setCreateOpen(false)} onDone={invalidate} />
      <EditModal target={editTarget} onClose={() => setEditTarget(null)} onDone={invalidate} />
      <ResetModal target={resetTarget} onClose={() => setResetTarget(null)} onDone={invalidate} />
    </>
  );
}

function CreateModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const mut = useMutation({
    mutationFn: createAdmin,
    onSuccess: () => { message.success('创建成功'); form.resetFields(); onDone(); onClose(); },
    onError: (e) => {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? '创建失败';
      message.error(msg);
    },
  });
  return (
    <Modal open={open} onCancel={onClose} onOk={() => form.submit()} title="新建用户" confirmLoading={mut.isPending} destroyOnClose>
      <Form form={form} layout="vertical" onFinish={(v) => mut.mutate(v)}>
        <Form.Item name="username" label="用户名" rules={[{ required: true, pattern: /^[a-zA-Z0-9_]{3,32}$/, message: '3-32 位字母/数字/下划线' }]}>
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item name="displayName" label="显示名"><Input autoComplete="off" /></Form.Item>
        <Form.Item name="role" label="角色" initialValue="operator" rules={[{ required: true }]}>
          <Select options={[{ value: 'admin', label: '管理员' }, { value: 'operator', label: '运营' }]} />
        </Form.Item>
        <Form.Item name="password" label="初始密码" rules={[{ required: true, min: 8, message: '至少 8 位且含字母与数字' }]}>
          <Input.Password autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function EditModal({ target, onClose, onDone }: { target: AdminRow | null; onClose: () => void; onDone: () => void }) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const mut = useMutation({
    mutationFn: (v: { displayName: string | null; role: AdminRow['role'] }) => patchAdmin(target!.id, v),
    onSuccess: () => { message.success('已保存'); onDone(); onClose(); },
    onError: (e) => message.error((e as { response?: { data?: { message?: string } } }).response?.data?.message ?? '保存失败'),
  });
  return (
    <Modal open={!!target} onCancel={onClose} onOk={() => form.submit()} title={`编辑 @${target?.username ?? ''}`} confirmLoading={mut.isPending} destroyOnClose>
      <Form form={form} layout="vertical" initialValues={target ?? {}} onFinish={(v) => mut.mutate(v)}>
        <Form.Item name="displayName" label="显示名"><Input /></Form.Item>
        <Form.Item name="role" label="角色" rules={[{ required: true }]}>
          <Select options={[{ value: 'admin', label: '管理员' }, { value: 'operator', label: '运营' }]} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function ResetModal({ target, onClose, onDone }: { target: AdminRow | null; onClose: () => void; onDone: () => void }) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const mut = useMutation({
    mutationFn: (v: { newPassword: string }) => resetAdminPassword(target!.id, v.newPassword),
    onSuccess: () => { message.success('密码已重置'); form.resetFields(); onDone(); onClose(); },
    onError: (e) => message.error((e as { response?: { data?: { message?: string } } }).response?.data?.message ?? '重置失败'),
  });
  return (
    <Modal open={!!target} onCancel={onClose} onOk={() => form.submit()} title={`重置 @${target?.username ?? ''} 的密码`} confirmLoading={mut.isPending} destroyOnClose>
      <Form form={form} layout="vertical" onFinish={(v) => mut.mutate(v)}>
        <Form.Item name="newPassword" label="新密码" rules={[{ required: true, min: 8, message: '至少 8 位且含字母与数字' }]}>
          <Input.Password autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
